import * as providerTrips from "./provider-trips.js";
import * as providerMultiSite from "./provider-multi-site.js";
import * as providerNotifications from "./provider-notifications.js";
import * as providerRoster from "./provider-roster.js";
import * as providerFleetPositions from "./provider-fleet-positions.js";
import * as providerOperatorProvision from "./provider-operator-provision.js";
import * as webProvision from "./web-provision.js";
// Generacion del document OpenAPI 3.1 a partir de los schemas zod y de la
// tabla de rutas (routes.ts). components/schemas se deriva de los exports con
// nombre (.openapi("Nombre")) de cada modulo del wire; paths se deriva de
// API_ROUTES. Es la frontera TS <-> KMP (ADR-002): mobile genera su cliente
// de dist/openapi.json (ver README.md).
import { OpenAPIRegistry, OpenApiGeneratorV31, getRefId, } from "@asteasolutions/zod-to-openapi";
import { z } from "zod";
import * as adminSession from "./admin-session.js";
import * as webSession from "./web-session.js";
import * as mobileTrips from "./mobile-trips.js";
import * as mobilePlaces from "./mobile-places.js";
import * as mobileAuth from "./mobile-auth.js";
import * as admin from "./admin.js";
import * as routing from "./routing.js";
import * as drivers from "./drivers.js";
import * as importWire from "./import.js";
import * as report from "./report.js";
import * as buses from "./buses.js";
import * as common from "./common.js";
import * as errors from "./errors.js";
import * as waivers from "./exceptions.js";
import * as metrics from "./metrics.js";
import * as health from "./health.js";
import * as rideRequests from "./ride-requests.js";
import * as tower from "./tower.js";
import * as trips from "./trips.js";
import * as siteParameters from "./site-parameters.js";
import * as recordPolicy from "./record-policy.js";
import * as recordHomeAddress from "./record-home-address.js";
import * as settlement from "./settlement.js";
import * as rateCards from "./rate-cards.js";
import * as tripRatings from "./trip-ratings.js";
import { ErrorResponse } from "./errors.js";
import { API_ROUTES } from "./routes.js";
import { CONTRACTS_VERSION } from "./version.js";
// Modulos del wire format. telemetry.ts queda afuera a proposito: modela lo
// que devuelve GPSWOX, no nuestra API.
const WIRE_MODULES = [
    adminSession,
    webSession,
    webProvision,
    providerOperatorProvision,
    mobileAuth,
    mobileTrips,
    mobilePlaces,
    providerTrips,
    providerMultiSite,
    providerNotifications,
    providerRoster,
    providerFleetPositions,
    common,
    errors,
    rideRequests,
    trips,
    buses,
    waivers,
    tower,
    metrics,
    routing,
    importWire,
    report,
    drivers,
    health,
    admin,
    siteParameters,
    recordPolicy,
    recordHomeAddress,
    settlement,
    rateCards,
    tripRatings,
];
/** Nombre con el que un schema se registra en components/schemas, si lo tiene. */
export function schemaName(schema) {
    return getRefId(schema);
}
function isZodSchema(value) {
    return value instanceof z.ZodType;
}
/**
 * Exports de schema de los modulos del wire que NO llevan nombre y por lo
 * tanto no van a components: son ladrillos (Integer, Decimal), no tipos. Todo
 * otro export sin nombre es un error (lo verifica openapi.test.ts).
 */
export const UNREGISTERED_SCHEMAS = new Set(["Integer", "Decimal"]);
/**
 * Exports zod sin nombre en los modulos del wire, para el test que exige que
 * cada export nuevo lleve .openapi("Nombre").
 */
export function exportsWithoutName() {
    const names = [];
    for (const wireModule of WIRE_MODULES) {
        for (const [exportName, value] of Object.entries(wireModule)) {
            if (isZodSchema(value) && schemaName(value) === undefined)
                names.push(exportName);
        }
    }
    return names;
}
function deriveRegisteredSchemas() {
    const registered = {};
    for (const wireModule of WIRE_MODULES) {
        for (const [exportName, value] of Object.entries(wireModule)) {
            if (!isZodSchema(value))
                continue;
            const name = schemaName(value);
            if (name === undefined)
                continue;
            if (name !== exportName) {
                throw new Error(`El export ${exportName} lleva .openapi("${name}"): el nombre del schema debe ser el del export`);
            }
            if (registered[name] !== undefined && registered[name] !== value) {
                throw new Error(`Schema ${name} exportado dos veces con instancias distintas`);
            }
            registered[name] = value;
        }
    }
    return registered;
}
/** Nombre en components/schemas -> schema zod. Derivado de los exports con nombre. */
export const registeredSchemas = deriveRegisteredSchemas();
const ERROR_DESCRIPTION = {
    429: "Demasiados intentos. Reintentar mas tarde.",
    503: "Autenticacion temporalmente no disponible.",
    400: "Body, params o query invalidos (validation) o contexto de planta faltante (missing_tenant).",
    401: "Token ausente o invalido (unauthenticated).",
    403: "Token sin claim de planta (no_tenant) o sin el rol que exige la ruta (missing_admin_role, missing_editor_role, missing_tower_role).",
    404: "Recurso inexistente en el tenant (not_found).",
    409: "Transicion de estado invalida o carrera perdida (conflict), o titular sin tercerizacion activa (subcontract_missing).",
    422: "Pedido bien formado que la regla de negocio rechaza (insufficient_lead_time con leadTime, outside_operating_area, service_not_available).",
};
const BEARER_SECURITY = "bearerAuth";
function registerRoute(registry, route) {
    const responses = {
        [String(route.response.status)]: {
            description: route.response.description,
            content: { "application/json": { schema: route.response.schema } },
        },
    };
    for (const status of route.errors) {
        responses[String(status)] = {
            description: ERROR_DESCRIPTION[status],
            content: { "application/json": { schema: ErrorResponse } },
        };
    }
    registry.registerPath({
        method: route.method.toLowerCase(),
        path: route.path,
        operationId: route.operationId,
        ...(route.deprecated === undefined ? {} : { deprecated: route.deprecated }),
        summary: route.summary,
        description: route.description,
        tags: [route.tag],
        ...(route.security === "none" ? {} : { security: [{ [route.security === "bearer_mobile" ? "mobileBearerAuth" : BEARER_SECURITY]: [] }] }),
        request: {
            ...(route.params !== undefined ? { params: route.params } : {}),
            ...(route.query !== undefined ? { query: route.query } : {}),
            ...(route.body !== undefined
                ? {
                    body: {
                        required: route.bodyOptional !== true,
                        content: { "application/json": { schema: route.body } },
                    },
                }
                : {}),
        },
        responses,
    });
}
/**
 * Genera el document OpenAPI 3.1 completo: components/schemas con todos los
 * schemas con nombre del wire format y paths con todas las rutas de la API.
 */
export function generateOpenApiDocument(routes = API_ROUTES) {
    const registry = new OpenAPIRegistry();
    for (const [name, schema] of Object.entries(registeredSchemas)) {
        registry.register(name, schema);
    }
    registry.registerComponent("securitySchemes", BEARER_SECURITY, {
        type: "http",
        scheme: "bearer",
        bearerFormat: "JWT",
        description: "ID token de Cognito administrativo. El tenant sale del claim custom:planta_id del token; /admin exige ademas el compartimento ucorp y rol ucorp_admin o ucorp_admin_reader.",
    });
    registry.registerComponent("securitySchemes", "mobileBearerAuth", { type: "http", scheme: "bearer", bearerFormat: "JWT", description: "ID token del pool mobile; no habilita rutas administrativas." });
    for (const route of routes) {
        registerRoute(registry, route);
    }
    const generator = new OpenApiGeneratorV31(registry.definitions);
    const document = generator.generateDocument({
        openapi: "3.1.0",
        info: {
            title: "UCORP API",
            version: CONTRACTS_VERSION,
            description: "Wire format de la plataforma UCORP (movilidad de personal para plantas industriales). API multi-tenant (ADR-001): todo recurso esta scopeado a una planta, que sale del token. Los errores llevan siempre el sobre ErrorResponse.",
        },
        tags: [
            { name: "mobileTrips", description: "Lecturas propias mobile, por planta y habilitacion activa." },
            { name: "mobilePlaces", description: "Busqueda de direcciones del pasajero por el proxy de la API (ADR-020)." },
            { name: "adminSession", description: "Sesion administrativa y habilitaciones activas." },
            { name: "webSession", description: "Alias deprecado de sesion administrativa." },
            { name: "mobileAuth", description: "Ingreso y sesion mobile, sin registro publico." },
            { name: "operational", description: "Salud y version, sin autenticacion." },
            { name: "trips", description: "Viajes del dia, ficha, recorrido y posiciones." },
            { name: "tower", description: "Gestion por excepcion: excepciones, avisos de demora y el panel de novedades (alerts)." },
            { name: "rideRequests", description: "Solicitudes de transporte: alta, listado y aviso de hoy no viajo." },
            { name: "import", description: "Importacion de solicitudes por lote y desde planilla." },
            { name: "metrics", description: "Metricas diarias del cierre." },
            { name: "report", description: "Informe de servicio (situacion inicial y mensual)." },
            { name: "routing", description: "Armado de tandas de viajes." },
            { name: "fleet", description: "Choferes y documentacion con vencimiento." },
            { name: "admin", description: "Backoffice interno de UCORP (cross-tenant)." },
        ],
    });
    withoutParameterDescriptions(document.paths);
    return document;
}
/**
 * zod-to-openapi 9 copia la description del schema de cada param de path y
 * query al Parameter Object; la 7 no lo hacia y el cliente Kotlin se genera de
 * este documento. Se quitan para que la migracion a zod 4 no mueva el wire:
 * publicarlas es un cambio de contrato aparte, con su version.
 */
function withoutParameterDescriptions(paths) {
    for (const pathItem of Object.values(paths ?? {})) {
        for (const operation of Object.values(pathItem)) {
            for (const parameter of operation.parameters ?? [])
                delete parameter.description;
        }
    }
}
//# sourceMappingURL=openapi.js.map